import React, { useEffect, useState } from 'react';
import { psaTypes } from 'src/constants';
import CustomSelect from 'src/components/MessengerSettings/CustomSelect';
import useAuthUser from 'src/hooks/useAuthUser';
import { chatgenieAPI } from 'src/config/api';
import { useMutation, useQuery } from 'react-query';
import queryKeys from 'src/constants/queryKeys';
import toast from 'react-hot-toast';
import { queryClient } from 'src/config/queryClient';
import { transformToCamelCase } from 'src/utils/data-transformer';
import { isHalo } from 'src/utils/psa';
import useIsFeatureFlagEnabled from 'src/hooks/useIsFeatureFlagEnabled';
import featureFlags from 'src/constants/featureFlags';
import * as Styled from '../../style';
import HaloPsaSettingsForm from '../HaloPsaSettingsForm';

const CWManageSettingsForm = () => {
  const { psaType } = useAuthUser();
  const isDefaultPriorityVisible = [psaTypes.AUTOTASK, psaTypes.HALO].includes(psaType);
  const isDefaultTicketTypeVisible = [psaTypes.HALO].includes(psaType);
  const isDefaultServiceSourceVisible = [psaTypes.CONNECTWISE, psaTypes.AUTOTASK].includes(psaType);
  const isApprovalsFeatureFlagEnabled = useIsFeatureFlagEnabled(featureFlags.APPROVALS);
  const isApprovalContactTypeVisible =
    [psaTypes.AUTOTASK].includes(psaType) && isApprovalsFeatureFlagEnabled;
  const { data: messengerSettings } = useQuery([queryKeys.GET_MESSENGER_SETTINGS], () =>
    chatgenieAPI.getMessengerSettings()
  );

  const getServiceSources = (query, page) =>
    chatgenieAPI.getSources({ query, page, normalizer: res => res.data });
  const getTicketTypes = (query, page) =>
    chatgenieAPI.getTicketTypes({ query, page, normalizer: res => res.data });
  const getPriorities = (query, page) =>
    chatgenieAPI.getPriorities({ query, page, normalizer: res => res.data });

  const getContactTypesUdf = () =>
    chatgenieAPI.autoTask.getContactTypesUdf({ normalizer: res => res.data });

  const [settingUpdating, setSettingUpdating] = useState('');

  const [data, setData] = useState({
    defaultServiceSource: null,
    defaultPriority: null,
    supportBoard: null,
    phoneServiceSource: null,
    emailServiceSource: null,
    contactTypeUdf: null
  });

  const { mutate: mutateSettings } = useMutation(
    data => {
      return chatgenieAPI.updateMessengerSettings(data);
    },
    {
      onSuccess: res => {
        queryClient.setQueryData([queryKeys.GET_MESSENGER_SETTINGS], res);
        setSettingUpdating('');
        toast.success('Successfully saved');
      },
      onError: error => {
        let errorMessage = error.response?.data?.message || error.message;

        if (error.response?.data?.errors) {
          errorMessage = Object.values(error.response?.data?.errors).reduce(
            (acc, error) => `${acc} ${error.join(' ')}`,
            `${errorMessage}`
          );
        }
        setSettingUpdating('');
        toast.error(errorMessage);
      }
    }
  );

  const handleWorkspaceSettingsChange = (value, e) => {
    setSettingUpdating(e.name);

    let key = null;
    if (e.name === 'default_service_source_id') {
      key = 'default_service_source';
    } else if (e.name === 'default_priority_id') {
      key = 'default_priority';
    } else if (e.name === 'default_ticket_type_id') {
      key = 'default_ticket_type';
    } else if (e.name === 'email_service_source_id') {
      key = 'email_service_source';
    } else if (e.name === 'phone_service_source_id') {
      key = 'phone_service_source';
    } else if (e.name === 'contact_type_udf_id') {
      key = 'contact_type_udf';
    }

    const newData = {
      [key]: {
        value: value.id,
        name: value.name
      }
    };

    const mutateData = {
      [e.name]: value.id
    };

    mutateSettings(mutateData);

    setData(old => ({
      ...old,
      ...transformToCamelCase(newData)
    }));
  };

  useEffect(() => {
    if (messengerSettings) {
      const normalizedSettingsData = messengerSettings && {
        ...transformToCamelCase(messengerSettings)
      };

      setData(normalizedSettingsData);
    }
  }, [messengerSettings]);
  return (
    <>
      <Styled.SettingsFormContent>
        <Styled.SettingTitle align="center" marginBottom="10">
          Settings
        </Styled.SettingTitle>
        <Styled.SettingSubTitle marginBottom="0">Workspace</Styled.SettingSubTitle>
        <Styled.DescriptionStyled>
          Manage your preferences for all members of this workspace
        </Styled.DescriptionStyled>
        {isDefaultServiceSourceVisible && (
          <Styled.FormGroup>
            <CustomSelect
              isLoading={settingUpdating === 'default_service_source_id'}
              name="default_service_source_id"
              value={data.defaultServiceSource}
              options={getServiceSources}
              onChange={handleWorkspaceSettingsChange}
              label="Chat source"
              componentType="paginate"
            />
          </Styled.FormGroup>
        )}
        {isDefaultPriorityVisible && (
          <Styled.FormGroup>
            <CustomSelect
              isLoading={settingUpdating === 'default_priority_id'}
              name="default_priority_id"
              value={data.defaultPriority}
              options={getPriorities}
              onChange={handleWorkspaceSettingsChange}
              label="Default priority"
              componentType="paginate"
            />
          </Styled.FormGroup>
        )}
        {isDefaultTicketTypeVisible && (
          <Styled.FormGroup>
            <CustomSelect
              isLoading={settingUpdating === 'default_ticket_type_id'}
              name="default_ticket_type_id"
              value={data.defaultTicketType}
              options={getTicketTypes}
              onChange={handleWorkspaceSettingsChange}
              label="Ticket types"
              componentType="paginate"
            />
          </Styled.FormGroup>
        )}
        <Styled.FormGroup>
          <CustomSelect
            isLoading={settingUpdating === 'email_service_source_id'}
            name="email_service_source_id"
            value={data.emailServiceSource}
            options={getServiceSources}
            onChange={handleWorkspaceSettingsChange}
            label="Email source"
            componentType="paginate"
          />
        </Styled.FormGroup>
        <Styled.FormGroup>
          <CustomSelect
            isLoading={settingUpdating === 'phone_service_source_id'}
            name="phone_service_source_id"
            value={data.phoneServiceSource}
            options={getServiceSources}
            onChange={handleWorkspaceSettingsChange}
            label="Phone source"
            componentType="paginate"
          />
        </Styled.FormGroup>
        {isApprovalContactTypeVisible && (
          <Styled.FormGroup>
            <CustomSelect
              isLoading={settingUpdating === 'contact_type_udf_id'}
              name="contact_type_udf_id"
              value={data.contactTypeUdf}
              options={getContactTypesUdf}
              onChange={handleWorkspaceSettingsChange}
              label="Approval Contact Type (UDF)"
              componentType="async"
            />
          </Styled.FormGroup>
        )}
        {isHalo() && (
          <>
            <hr />
            <HaloPsaSettingsForm />
          </>
        )}
      </Styled.SettingsFormContent>
    </>
  );
};

export default CWManageSettingsForm;
